"use client";

import { useEffect } from "react";
import { karla, shrikhand, spaceMono } from "./fonts";
// global-error REPLACES app/layout.tsx when it fires — none of that layout's assets
// ship with it, including this stylesheet. Without this import the page renders as
// unstyled black-on-white markup, which is the single most common way this file is
// gotten wrong.
import "./globals.css";

// app/layout.tsx puts these three .variable classNames on <html>, and every font
// declaration in globals.css routes through var(--font-karla) / var(--font-shrikhand)
// / var(--font-space-mono) — next/font/local registers each face under a family name
// of its own choosing, reachable only through its custom property. This file renders its own
// <html>, so it hangs all three (imported from ./fonts) on it too, or the FLARE
// wordmark falls back to a system cursive and the body copy to system-ui, on the one
// page nobody chose to visit.

/**
 * Root-layout error boundary: fires when app/layout.tsx itself throws, or when a throw
 * has no error.tsx boundary above it to land in. It fully replaces the root layout, so
 * it must render its own <html>/<body>.
 *
 * Styled from flare's own tokens rather than the canonical copy's neutral cream, and
 * built entirely out of classes app/globals.css already defines — the sign-in screen's
 * masthead and body shell, which is the app's other standalone full-page surface. No
 * new CSS, so test/css-classes.test.ts stays satisfied and the stylesheet stays put.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    void (async () => {
      try {
        const { reportError } = await import("@/lib/flare");
        await reportError(error, { kind: "client", digest: error.digest });
      } catch {
        // The page is already broken; a failed report must not make it worse.
      }
    })();
  }, [error]);

  return (
    <html lang="en" className={`${karla.variable} ${shrikhand.variable} ${spaceMono.variable}`}>
      <head>
        {/* Next's global-error path bypasses metadata/viewport resolution entirely (its
            own DefaultGlobalError has the same gap), so without this an installed PWA
            renders this page at desktop width — exactly the scenario it exists for. */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </head>
      <body className={karla.className}>
        {/* The wordmark is the logotype, not this page's heading — unlike /login, which
            has no other title to give the <h1> to. Here the <h1> belongs to the thing
            the reader came to find out, so the mark renders as a <p>. */}
        <header className="triage-top signin-top">
          <p className="triage-word">FLARE</p>
          <p className="signin-tagline">Fleet error triage</p>
        </header>
        <main className="signin-body">
          {/* .empty-title is the app's one plain page-title rule (700 1.4rem Karla on
              ink) and it is not empty-state-specific in anything but its name. */}
          <h1 className="empty-title">flare hit an error</h1>
          <p className="signin-lede">
            This page stopped rendering, and the error was reported automatically. Trying
            again usually clears it.
          </p>
          {/* .signin-go is the fleet 44px tap target: acid on ink, 16.34:1. */}
          <button type="button" className="signin-go" onClick={reset}>
            Try again
          </button>
        </main>
      </body>
    </html>
  );
}
